import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ApiError, api } from '../lib/api';
import { haptic } from '../lib/telegram';

/**
 * Internal admin panel — surfaces the /dev/* endpoints as buttons. Server-side
 * gating by DEV_SHORTCUT_ENABLED=1 means non-dev environments respond 403, and
 * we render a "disabled" notice in that case rather than fake-show buttons.
 */
export function Admin() {
  const qc = useQueryClient();
  const state = useQuery({
    queryKey: ['dev', 'me'],
    queryFn: () => api.devMe(),
    retry: false,
  });

  function flush() {
    void qc.invalidateQueries({ queryKey: ['dev', 'me'] });
    void qc.invalidateQueries({ queryKey: ['me'] });
    void qc.invalidateQueries({ queryKey: ['subscription'] });
    void qc.invalidateQueries({ queryKey: ['credit'] });
  }

  const grantPremium = useMutation({
    mutationFn: (days: number) => api.devGrantPremium(days),
    onSuccess: () => {
      haptic.success();
      flush();
    },
    onError: () => haptic.error(),
  });
  const revokePremium = useMutation({
    mutationFn: () => api.devRevokePremium(),
    onSuccess: () => {
      haptic.success();
      flush();
    },
    onError: () => haptic.error(),
  });
  const grantCredit = useMutation({
    mutationFn: (amount: number) => api.devGrantCredit(amount),
    onSuccess: () => {
      haptic.success();
      flush();
    },
    onError: () => haptic.error(),
  });

  const disabled = state.error instanceof ApiError && state.error.status === 403;

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <header>
        <div className="font-display-alt text-xs uppercase tracking-widest text-crimson">
          Internal · dev only
        </div>
        <h1 className="font-display text-3xl uppercase tracking-wide mt-1">Admin</h1>
      </header>

      {disabled && (
        <Card className="border-crimson/40">
          <div className="font-chunky text-sm text-crimson">Dev endpoints disabled</div>
          <div className="text-xs text-text-dim mt-1">
            The api Worker has <code className="font-mono text-trophy-gold">DEV_SHORTCUT_ENABLED</code>{' '}
            unset. Flip it to <code className="font-mono">"1"</code> in the wrangler.toml and redeploy
            to use this panel.
          </div>
        </Card>
      )}

      {state.data && (
        <Card>
          <div className="font-display-alt text-xs uppercase tracking-widest text-text-faint mb-2">
            Current state
          </div>
          <div className="space-y-1 text-sm">
            <Row label="User ID" value={String(state.data.userId)} />
            <Row label="Stars credit" value={`${state.data.starsCredit} ⭐`} />
            <Row label="Bracket Premium" value={state.data.hasBracketPremium ? 'yes' : 'no'} />
            <Row
              label="Premium active"
              value={
                state.data.premiumActive
                  ? `until ${new Date((state.data.premiumUntil ?? 0) * 1000).toLocaleString('en-GB')}`
                  : 'no'
              }
            />
          </div>
        </Card>
      )}

      {!disabled && (
        <>
          <Section title="Premium subscription">
            <Card className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="primary"
                  fullWidth
                  disabled={grantPremium.isPending}
                  onClick={() => grantPremium.mutate(1)}
                >
                  Grant 1 day
                </Button>
                <Button
                  variant="primary"
                  fullWidth
                  disabled={grantPremium.isPending}
                  onClick={() => grantPremium.mutate(30)}
                >
                  Grant 30 days
                </Button>
              </div>
              <Button
                variant="danger"
                fullWidth
                disabled={revokePremium.isPending}
                onClick={() => revokePremium.mutate()}
              >
                Revoke premium
              </Button>
            </Card>
          </Section>

          <Section title="Stars credit">
            <Card>
              <div className="grid grid-cols-3 gap-2">
                {[100, 500, 2000].map((amt) => (
                  <Button
                    key={amt}
                    variant="primary"
                    fullWidth
                    disabled={grantCredit.isPending}
                    onClick={() => grantCredit.mutate(amt)}
                  >
                    +{amt} ⭐
                  </Button>
                ))}
              </div>
            </Card>
          </Section>

          <MetricsSection />
          <BroadcastSection />

          <Section title="Next steps">
            <Card>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/bracket" className="text-trophy-gold">
                    Bracket Premium flow →
                  </Link>
                </li>
                <li>
                  <Link to="/today" className="text-trophy-gold">
                    Boost / Insurance flow on a fixture →
                  </Link>
                </li>
                <li>
                  <Link to="/settings" className="text-trophy-gold">
                    Premium subscribe in Settings →
                  </Link>
                </li>
              </ul>
            </Card>
          </Section>
        </>
      )}

      <Link
        to="/settings"
        className="block text-center font-display-alt text-xs uppercase tracking-widest text-text-dim pt-2"
      >
        ← Back to Settings
      </Link>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display-alt text-xs uppercase tracking-widest text-text-faint mb-1.5">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-text-dim">{label}</span>
      <span className="font-mono text-text-base text-xs text-right">{value}</span>
    </div>
  );
}

function MetricsSection() {
  const m = useQuery({
    queryKey: ['admin', 'metrics'],
    queryFn: () => api.adminMetrics(),
    refetchInterval: 30_000,
    retry: false,
  });

  // 403 here means the user isn't on the telegram_id allowlist. Hide silently —
  // the dev panel is already shown to anyone who hits /admin, but the metrics +
  // broadcast cards are admin-only and stay hidden for everyone else.
  if (m.error instanceof ApiError && m.error.status === 403) return null;

  return (
    <Section title="Launch metrics · last 24h">
      <Card>
        {m.isLoading && <div className="text-xs text-text-dim">Loading…</div>}
        {m.error && !(m.error instanceof ApiError && m.error.status === 403) && (
          <div className="text-xs text-crimson">Error: {String(m.error)}</div>
        )}
        {m.data && (
          <div className="space-y-1 text-sm">
            <Row label="Total users" value={String(m.data.metrics.total_users)} />
            <Row label="New today" value={String(m.data.metrics.new_today)} />
            <Row label="DAU (24h)" value={String(m.data.metrics.dau_24h)} />
            <Row label="Predictions (24h)" value={String(m.data.metrics.preds_24h)} />
            <Row label="Predictions today" value={String(m.data.metrics.preds_today)} />
            <Row label="Scored (24h)" value={String(m.data.metrics.scored_24h)} />
            <Row label="Fixtures today" value={String(m.data.metrics.fixtures_today)} />
            <Row label="Stars (24h)" value={`${m.data.metrics.stars_24h} ⭐`} />
            <Row label="Stars today" value={`${m.data.metrics.stars_today} ⭐`} />
            <Row label="Bracket holders" value={String(m.data.metrics.bracket_holders)} />
            <Row label="Premium subs" value={String(m.data.metrics.premium_subscribers)} />
          </div>
        )}
        <div className="mt-2 text-[10px] font-mono text-text-faint">
          {m.data ? `fetched ${new Date(m.data.fetchedAt * 1000).toLocaleTimeString('en-GB')}` : ''}
          {' · auto-refresh 30s'}
        </div>
      </Card>
    </Section>
  );
}

function BroadcastSection() {
  const [text, setText] = useState('');
  const [kind, setKind] = useState('broadcast');
  const [bucketKey, setBucketKey] = useState('');
  const [btnText, setBtnText] = useState('');
  const [btnUrl, setBtnUrl] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const broadcast = useMutation({
    mutationFn: () =>
      api.adminBroadcast({
        text,
        kind: kind || undefined,
        bucketKey: bucketKey || undefined,
        miniAppButton: btnText && btnUrl ? { text: btnText, url: btnUrl } : undefined,
      }),
    onSuccess: (r) => {
      haptic.success();
      setLastResult(
        r.ok
          ? `Sent to audience of ${r.audienceSize ?? '?'} (kind=${r.kind}, bucket=${r.bucketKey})`
          : `Error: ${r.error ?? 'unknown'}`,
      );
      setConfirming(false);
    },
    onError: (err) => {
      haptic.error();
      setLastResult(`Error: ${String(err)}`);
      setConfirming(false);
    },
  });

  // Probe permission. If 403, hide the section entirely.
  const probe = useQuery({
    queryKey: ['admin', 'metrics'],
    queryFn: () => api.adminMetrics(),
    retry: false,
  });
  if (probe.error instanceof ApiError && probe.error.status === 403) return null;

  const canSend = text.length > 0 && text.length <= 4000 && !broadcast.isPending;

  return (
    <Section title="Broadcast DM">
      <Card className="space-y-2">
        <label className="block text-xs text-text-dim">
          Message (max 4000 chars · {text.length})
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            placeholder="Write the DM…"
            className="mt-1 w-full rounded bg-bg-card-2 border border-bg-card-3 p-2 text-sm font-mono text-text-base"
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs text-text-dim">
            Kind
            <input
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="mt-1 w-full rounded bg-bg-card-2 border border-bg-card-3 p-1.5 text-sm font-mono"
              placeholder="broadcast"
            />
          </label>
          <label className="block text-xs text-text-dim">
            Bucket key (optional)
            <input
              value={bucketKey}
              onChange={(e) => setBucketKey(e.target.value)}
              className="mt-1 w-full rounded bg-bg-card-2 border border-bg-card-3 p-1.5 text-sm font-mono"
              placeholder="defaults to UTC date"
            />
          </label>
          <label className="block text-xs text-text-dim">
            Button label (optional)
            <input
              value={btnText}
              onChange={(e) => setBtnText(e.target.value)}
              className="mt-1 w-full rounded bg-bg-card-2 border border-bg-card-3 p-1.5 text-sm font-mono"
              placeholder="Open PunditXI"
            />
          </label>
          <label className="block text-xs text-text-dim">
            Button URL (optional)
            <input
              value={btnUrl}
              onChange={(e) => setBtnUrl(e.target.value)}
              className="mt-1 w-full rounded bg-bg-card-2 border border-bg-card-3 p-1.5 text-sm font-mono"
              placeholder="https://punditxi.app"
            />
          </label>
        </div>

        {!confirming ? (
          <Button
            variant="primary"
            fullWidth
            disabled={!canSend}
            onClick={() => setConfirming(true)}
          >
            Review &amp; send →
          </Button>
        ) : (
          <div className="space-y-2 rounded border border-trophy-gold/40 p-2">
            <div className="text-xs text-trophy-gold font-chunky">
              About to send to ALL users with notifications on.
            </div>
            <div className="text-xs text-text-dim">
              Dedupe key: <code className="font-mono">{kind}</code> /{' '}
              <code className="font-mono">{bucketKey || '(today)'}</code>. Users already-sent under
              this key will be skipped.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="danger" fullWidth onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                fullWidth
                disabled={broadcast.isPending}
                onClick={() => broadcast.mutate()}
              >
                {broadcast.isPending ? 'Sending…' : 'Confirm send'}
              </Button>
            </div>
          </div>
        )}

        {lastResult && (
          <div className="text-xs font-mono text-text-dim border-t border-bg-card-3 pt-2">
            {lastResult}
          </div>
        )}
      </Card>
    </Section>
  );
}
